import { displayText } from '../../terminology/store';
import { useTerminology } from '../../terminology/TerminologyContext';
/* JoinSession.jsx */
import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  memo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeContext from '../ThemeContext';
import Layout from '../Layout';
import { toast } from 'react-toastify';
import { sendSessionInvite, shareSessionDocuments } from '../services/api';
import {
  getCase,
  getDeposition,
  getArbitration,
  getTrial,
  getMediation,
  getWitnessPrep,
  getCustomSession, //  add this
  getCaseFolders,
  getFolderDocuments,
  getSessionStatus,
  startSession,
  finishSession,
  getSessionParticipants,
  setSessionDocument,
  passEditingRights,
  revokeEditingRights,
  getSessionZoomDetails,
  createSessionZoomMeeting,
  getZoomOAuthStatus,
  connectZoomOAuth,
} from '../services/api';
import {
  getCheckedFoldersKey,
  getCheckedDocumentsKey,
} from '../Utils/storageKeys';
import 'bootstrap-icons/font/bootstrap-icons.css';

const defaultTheme = {
  name: 'default',
  colors: {
    primary: '#f97316',
    secondary: '#6c757d',
    bgPrimary: '#ffffff',
    textWhite: '#ffffff',
    textPrimary: '#000000',
    borderColor: '#dee2e6',
    success: '#28a745',
    danger: '#dc3545',
    warning: '#ffc107',
  },
  gradients: { primary: '#0d6efd', secondary: '#6c757d' },
  typography: {
    fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
    titleFontSize: 24, // px
    bodyFontSize: 14,  // px
  },
};

/* Popup Component – Reused from Layout.js (exact styles) */
const Popup = memo(({
  show,
  onClose,
  title,
  children,
  onConfirm,
  confirmText = 'Join',
  cancelText = 'Cancel',
  theme = defaultTheme,   // ✅ ADD THIS
  isLoading,
  showOnlyConfirm = false,
}) => {
  useTerminology();
    if (!show) return null;
    return (
      <div
        className="popup-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050,
        }}
      >
        <div
          className="popup-box"
          style={{
            backgroundColor: theme.colors.bgPrimary,
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            textAlign: 'center',
            maxWidth: '460px',
            width: '90%',
            minHeight: '280px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h5
              style={{
                color: theme.colors.textWhite,
                backgroundColor: theme.colors.primary,
                padding: '10px 40px',
                borderRadius: '5px',
                display: 'inline-block',
                width: '400px',
                maxWidth: '100%',
                margin: '0 auto 15px',
                boxSizing: 'border-box',
              }}
            >
              {title}
            </h5>
            {children}
          </div>
          <div
            style={{
              marginTop: '20px',
              display: 'flex',
              justifyContent: showOnlyConfirm ? 'center' : 'space-between',
              gap: '10px',
            }}
          >
            {!showOnlyConfirm && (
              <button
                className="btn btn-secondary"
                style={{
                  backgroundColor: theme.colors.secondary,
                  color: theme.colors.textWhite,
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  width: '48%',
                  fontSize: 'clamp(0.78rem, 1.9vw, 0.88rem)',
                }}
                onClick={onClose}
              >
                {cancelText}
              </button>
            )}
            <button
              className="btn btn-primary"
              style={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.textWhite,
                border: 'none',
                padding: '8px 16px',
                borderRadius: '5px',
                cursor: 'pointer',
                width: showOnlyConfirm ? 'auto' : '48%',
                minWidth: showOnlyConfirm ? '120px' : 'auto',
                fontSize: 'clamp(0.78rem, 1.9vw, 0.88rem)',
              }}
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? 'Validating...' : confirmText}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

const JoinSession = () => {
  useTerminology();
  const themeContext = useContext(ThemeContext);
const theme = themeContext?.theme || defaultTheme;

  // Typography from theme (client-admin theme)
  const fontFamily =
    theme.typography?.fontFamily || defaultTheme.typography.fontFamily;
  const bodyFontSize =
    theme.typography?.bodyFontSize || defaultTheme.typography.bodyFontSize;
  const titleFontSize =
    theme.typography?.titleFontSize || defaultTheme.typography.titleFontSize;

  const navigate = useNavigate();
  /* persistent data */
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));
  const [sessionDetails, setSessionDetails] = useState(() =>
    JSON.parse(localStorage.getItem('currentSession') || 'null')
  );
  /* UI state */
  const [caseData, setCaseData] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [folderSortField, setFolderSortField] = useState('name');
  const [folderSortOrder, setFolderSortOrder] = useState('asc');
  const [documentSortField, setDocumentSortField] = useState('name');
  const [documentSortOrder, setDocumentSortOrder] = useState('asc');
  const [showAttendance, setShowAttendance] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionStatus, setSessionStatus] = useState('unknown');
  // Editor management state
  const [currentEditor, setCurrentEditor] = useState(null);
  const [selectedEditorId, setSelectedEditorId] = useState(null);
  // Selected document row
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  /* POPUPS */
  const [showStartPopup, setShowStartPopup] = useState(false);
  const [showFinishConfirmPopup, setShowFinishConfirmPopup] = useState(false);
  const [showFinishSuccessPopup, setShowFinishSuccessPopup] = useState(false);
  const [showPasscodePopup, setShowPasscodePopup] = useState(false);
  const passcodeIconRef = useRef(null);
  const [passcodePos, setPasscodePos] = useState({ top: 0, left: 0 });
  const [folderHeaderLabel, setFolderHeaderLabel] = useState('Name');
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [documentHeaderLabel, setDocumentHeaderLabel] = useState('Name');
  const [showDocumentHeaderDropdown, setShowDocumentHeaderDropdown] =
    useState(false);
  const folderHeaderRef = useRef(null);
  const documentHeaderRef = useRef(null);
  const bcRef = useRef(null);
  //Zoom
// Zoom


const [showZoomPopup, setShowZoomPopup] = useState(false);
const [zoomLaunchUrl, setZoomLaunchUrl] = useState('');
const [zoomMeetingInfo, setZoomMeetingInfo] = useState(null);
const [inviteRole, setInviteRole] = useState('witness');
const sessionPasscode = sessionData?.session_passcode;
const [showInvitePopup, setShowInvitePopup] = useState(false);
const [inviteEmails, setInviteEmails] = useState('');
const [inviteMessage, setInviteMessage] = useState('');
const [sendingInvite, setSendingInvite] = useState(false);
const canControl = user?.role === 'client_admin';
const [showShareDocsPopup, setShowShareDocsPopup] = useState(false);
const [selectedDocs, setSelectedDocs] = useState([]);
const [sendingDocs, setSendingDocs] = useState(false);

const showInviteButton =
  canControl && sessionStatus !== 'finished';

const isZoomSession = () => {
  return (
    !!sessionData?.schedule_video &&
    String(sessionData?.zoom_tech || '').toLowerCase() === 'zoom'
  );
};

const getErrorMessage = (err) => {
  const errorData = err?.response?.data?.error;
  if (typeof errorData === 'string') return errorData;
  if (errorData?.message) return errorData.message;
  return (
    err?.response?.data?.message ||
    err?.message ||
    'Something went wrong'
  );
};

const loadOrCreateZoomMeeting = async () => {
  try {
    let res;

    try {
      res = await getSessionZoomDetails(sessionDetails.sessionId);
    } catch (err) {
      const message = err?.response?.data?.message || '';

      if (
        err?.response?.status === 400 &&
        message === 'Zoom meeting is not created yet'
      ) {
        console.log('Zoom not created yet. Creating new Zoom meeting...');
        res = await createSessionZoomMeeting(sessionDetails.sessionId);
      } else {
        throw err;
      }
    }

    const data = res?.data?.data || {};

    const hostUrl = data.zoom_start_url || data.zoom_join_url;

if (!hostUrl) {
  throw new Error('Zoom host link not available');
}

setZoomLaunchUrl(hostUrl);
setZoomMeetingInfo(data);
setShowZoomPopup(true);

    return data;
  } catch (err) {
    console.error('Zoom load/create failed:', err);
    const backendMessage = getErrorMessage(err);
    toast.error(backendMessage);
    throw err;
  }
};

const beginSession = async () => {
  if (user?.role !== 'client_admin') return;

  setIsLoading(true);

  try {
    const zoomEnabled = isZoomSession();

    if (zoomEnabled) {
  const statusRes = await getZoomOAuthStatus();
  const connected = !!statusRes?.data?.connected;

  if (!connected) {
    toast.info('Redirecting to Zoom authorization...');
    await connectZoomOAuth();
    return;
  }

  await loadOrCreateZoomMeeting();

  await startSession(sessionDetails.sessionId);
  setSessionStatus('started');
  toast.success(displayText('Session started!'), { autoClose: 3000 });
  await loadParticipants();

  return;
}

    await startSession(sessionDetails.sessionId);
    setSessionStatus('started');
    toast.success(displayText('Session started!'), { autoClose: 3000 });
    await loadParticipants();
    setShowStartPopup(true);
  } catch (err) {
    toast.error(getErrorMessage(err));
  } finally {
    setIsLoading(false);
  }
};

  /* SESSION VALIDATION */
  useEffect(() => {
    const sess = JSON.parse(localStorage.getItem('currentSession') || 'null');
    if (!sess?.sessionId || !sess?.type || !sess?.userRole) {
      toast.error(displayText('Invalid session. Redirecting to sessions...'), {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      navigate('/session', { replace: true });
      return;
    }
    setSessionDetails(sess);
  }, [navigate, theme, fontFamily, bodyFontSize]);

  /* BroadcastChannel listener for participant & editor changes */
  useEffect(() => {
    try {
      bcRef.current = new BroadcastChannel('presentation_channel');
      bcRef.current.onmessage = (ev) => {
        const msg = ev.data || {};
        if (
          msg.type === 'participant_left' &&
          String(msg.sessionId) === String(sessionDetails?.sessionId)
        ) {
          setParticipants((prev) =>
            prev.filter((p) => String(p.id) !== String(msg.userId))
          );
        } else if (
          msg.type === 'editor_changed' &&
          String(msg.sessionId) === String(sessionDetails?.sessionId)
        ) {
          const editor = msg.current_editor || null;
          setCurrentEditor(editor);
          try {
            localStorage.setItem(
              `presentation_editor_${sessionDetails.sessionId}`,
              JSON.stringify(editor)
            );
          } catch (e) {}
          setParticipants((prev) =>
            prev.map((p) => ({
              ...p,
              isEditor: editor && String(p.id) === String(editor.id),
            }))
          );
        }
      };
    } catch (e) {
      // BroadcastChannel not supported
    }
    return () => {
      if (bcRef.current) {
        try {
          bcRef.current.close();
        } catch (_) {}
      }
    };
  }, [sessionDetails?.sessionId]);


  /* LOAD SESSION + FOLDERS */
  useEffect(() => {
    if (!sessionDetails?.sessionId) return;
    let isMounted = true;
    const loadSession = async () => {
  try {
    setIsLoading(true);

    // 1. Get session status & basic info
    const statusRes = await getSessionStatus(sessionDetails.sessionId);
    const status = statusRes.data?.status || 'unknown';
    setSessionStatus(status);

    // Initialize current editor from API or localStorage
    const editorFromApi = statusRes.data?.current_editor ?? null;
    if (editorFromApi) {
      setCurrentEditor(editorFromApi);
      localStorage.setItem(
        `presentation_editor_${sessionDetails.sessionId}`,
        JSON.stringify(editorFromApi)
      );
    } else {
      try {
        const stored = localStorage.getItem(
          `presentation_editor_${sessionDetails.sessionId}`
        );
        if (stored) {
          const parsed = JSON.parse(stored);
          setCurrentEditor(parsed);
        }
      } catch (e) {}
    }

    // 2. Get session details
    // 2. Get session details
const sessionTypeMap = {
  deposition: getDeposition,
  arbitration: getArbitration,
  trial: getTrial,
  mediation: getMediation,
  'witness-prep': getWitnessPrep,
  custom: getCustomSession,
};

const api = sessionTypeMap[sessionDetails.type];
if (!api) throw new Error(`Unknown session type: ${sessionDetails.type}`);

const sessionResponse = await api(sessionDetails.sessionId);
if (!sessionResponse?.data) throw new Error('Session not found');

const session = sessionResponse.data?.data || sessionResponse.data;

    // ────────────────  Safety check ───────────────────
    if (!session.case_id || isNaN(Number(session.case_id))) {
      throw new Error(
        `Session has invalid or missing case_id: ${session.case_id}`
      );
    }
    // ──

    setSessionData(session);

    // 3. Load case
    const caseRes = await getCase(session.case_id);
    setCaseData(caseRes.data?.data || null);

    // 4. Load folders
    const folderRes = await getCaseFolders(
  String(session.case_id),
  sessionDetails.sessionId
);
    const allFolders = folderRes.data || [];

    const joinKey = getCheckedFoldersKey(
      String(session.case_id),
      sessionDetails.sessionId
    );

    const selectedIds = JSON.parse(localStorage.getItem(joinKey) || '[]').map(
      Number
    );

    const filteredFolders = allFolders
      .filter((f) => selectedIds.includes(f.id))
      .map((f) => ({ ...f, isSelected: true }));

    if (!isMounted) return;

    setFolders(filteredFolders);
    setSelectedFolder(filteredFolders[0] || null);

    if (status === 'started') {
      await loadParticipants();
    }
  } catch (e) {
    if (!isMounted) return;

    console.error('Session load error:', e);

    const errorMessage = e.message.includes('case_id')
      ? 'This session is not linked to any case. Contact support.'
      : 'Failed to load session data.';

    toast.error(errorMessage, { autoClose: 5000 });

    setTimeout(() => navigate('/session', { replace: true }), 1800);
  } finally {
    if (isMounted) setIsLoading(false);
  }
};
    loadSession();
    return () => {
      isMounted = false;
    };
  }, [sessionDetails, navigate]);

  /* PARTICIPANTS */
  const loadParticipants = async () => {
    try {
      const res = await getSessionParticipants(sessionDetails.sessionId);
      const rawParticipants = res.data?.participants || [];
      const currentUser = {
        id: user?.id || 'current-user',
        full_name: user?.full_name || user?.username || 'You',
        email: user?.email || 'N/A',
        type: user?.role === 'client_admin' ? 'client_admin' : 'witness',
      };
      const notLeft = rawParticipants.filter((p) => {
        const leftKey = `left_session_${sessionDetails.sessionId}_${String(
          p.id
        )}`;
        try {
          return !localStorage.getItem(leftKey);
        } catch (e) {
          return true;
        }
      });
      let participantsList = notLeft.filter(
        (p) => String(p.id) !== String(currentUser.id)
      );
      const currentLeftKey = `left_session_${sessionDetails.sessionId}_${String(
        currentUser.id
      )}`;
      const currentHasLeft = (() => {
        try {
          return !!localStorage.getItem(currentLeftKey);
        } catch (e) {
          return false;
        }
      })();
      if (
        !participantsList.some(
          (p) => String(p.id) === String(currentUser.id)
        ) &&
        !currentHasLeft
      ) {
        participantsList.unshift(currentUser);
      }
      const sortedParticipants = participantsList
        .sort((a, b) => {
          const order = { client_admin: 0, witness: 1, guest: 2 };
          return (order[a.type] ?? 3) - (order[b.type] ?? 3);
        })
        .map((p) => ({
          ...p,
          isEditor: currentEditor && String(p.id) === String(currentEditor.id),
        }));
      setParticipants(sortedParticipants);
    } catch (err) {
      console.error('Error loading participants:', err);
      setParticipants([]);
    }
  };

  /* DOCUMENTS */
  useEffect(() => {
    if (!selectedFolder) {
      setDocuments([]);
      return;
    }
    const fetchDocs = async () => {
      try {
        const all = await getFolderDocuments(selectedFolder.id);
        const allDocs = all.data || [];
        const docKey = getCheckedDocumentsKey(
          selectedFolder.id,
          sessionDetails.sessionId
        );
        const selIds = JSON.parse(localStorage.getItem(docKey) || '[]').map(
          Number
        );
        const filtered = allDocs
          .filter((d) => selIds.includes(d.id))
          .map((d) => ({
            ...d,
            // Normalize created_at for sorting (fallback to updated_at)
            created_at: d.created_at || d.updated_at || null,
          }));
        setDocuments(filtered);
      } catch {
        setDocuments([]);
      }
    };
    fetchDocs();
  }, [selectedFolder, sessionDetails?.sessionId]);

  /* ONLYOFFICE NAVIGATION */
  const openInPresentation = async (doc) => {
    if (sessionStatus !== 'started') {
      toast.warn(displayText('Session must be started to view documents.'));
      return;
    }
    if (doc.is_placeholder) {
      toast.warn('Upload a real file first.');
      return;
    }
    const payload = {
      docId: doc.id,
      docName: doc.name,
      sessionId: sessionDetails.sessionId,
      caseId: sessionData?.case_id,
      openedAt: new Date().toISOString(),
    };
    const key = `presentation_${sessionDetails.sessionId}`;
    setIsLoading(true);
    try {
      await setSessionDocument(sessionDetails.sessionId, {
        document_id: doc.id,
        doc_name: doc.name,
      });
      localStorage.setItem(key, JSON.stringify(payload));
      try {
        sessionStorage.setItem(key, JSON.stringify(payload));
      } catch (e) {}
      try {
        const bc = new BroadcastChannel('presentation_channel');
        bc.postMessage(payload);
        bc.close();
      } catch (e) {}
      navigate('/presentation', { state: { ...payload } });
      toast.success('Presentation opened.');
    } catch (err) {
      console.error('Failed to persist session document:', err);
      toast.error('Failed to open presentation.');
    } finally {
      setIsLoading(false);
    }
  };


  /* Editor Management */

  // 🔒 is editing locked to the current editor (after Pass is pressed)?
  const editingLocked =
    canControl &&
    sessionStatus === 'started' &&
    currentEditor &&
    currentEditor.type !== 'client_admin';

  // Before Pass: toggle select/deselect.
  // After Pass: do nothing (can't deselect; only Revoke resets).
  const handleSelectEditor = (participant) => {
    if (participant.type === 'client_admin' || !canControl) return;

    if (editingLocked) {
      // Once editing is passed, selection is locked until Revoke.
      return;
    }

    setSelectedEditorId((prev) =>
      prev != null && String(prev) === String(participant.id)
        ? null // allow deselect before Pass
        : participant.id
    );
  };

  const broadcastEditorChange = (editor) => {
    const payload = {
      type: 'editor_changed',
      sessionId: sessionDetails.sessionId,
      current_editor: editor,
    };
    try {
      localStorage.setItem(
        `presentation_editor_${sessionDetails.sessionId}`,
        JSON.stringify(editor)
      );
    } catch (e) {}
    try {
      const bc = bcRef.current || new BroadcastChannel('presentation_channel');
      bc.postMessage(payload);
      if (!bcRef.current) bc.close();
    } catch (e) {}
  };

  const handlePassEditing = async () => {
    if (!canControl || sessionStatus !== 'started') {
      toast.warn(
        displayText('Session must be started and you must be admin to pass editing rights.')
      );
      return;
    }
    if (!selectedEditorId) {
      toast.warn('Select a participant to pass editing rights to.');
      return;
    }
    const participant = participants.find(
      (p) => String(p.id) === String(selectedEditorId)
    );
    if (!participant) {
      toast.error('Selected participant not found.');
      return;
    }
    if (participant.type === 'client_admin') {
      toast.warn(displayText('Client admin already has editing rights.'));
      return;
    }
    try {
      setIsLoading(true);
      const res = await passEditingRights(sessionDetails.sessionId, {
        join_request_id: participant.id,
      });
      const editor = res.data?.current_editor || {
        id: participant.id,
        name: participant.full_name,
        type: participant.type,
      };
      setCurrentEditor(editor);
      setParticipants((prev) =>
        prev.map((p) => ({
          ...p,
          isEditor: String(p.id) === String(editor.id),
        }))
      );
      broadcastEditorChange(editor);
      toast.success(`Editing rights passed to ${editor.name}.`);
    } catch (err) {
      console.error('Pass editing rights failed', err);
      toast.error(
        err.response?.data?.error || 'Failed to pass editing rights.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeEditing = async () => {
    if (!canControl || sessionStatus !== 'started') {
      toast.warn(
        displayText('Session must be started and you must be admin to revoke editing rights.')
      );
      return;
    }
    try {
      setIsLoading(true);
      await revokeEditingRights(sessionDetails.sessionId);
      setCurrentEditor(null);
      setParticipants((prev) => prev.map((p) => ({ ...p, isEditor: false })));
      broadcastEditorChange(null);
      // Clear checkbox selection only on Revoke
      setSelectedEditorId(null);
      toast.info(displayText('Editing rights returned to client admin.'));
    } catch (err) {
      console.error('Revoke editing rights failed', err);
      toast.error(
        err.response?.data?.error || 'Failed to revoke editing rights.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  /* SORTING HELPERS */
  const toggleFolderSortOrder = () => {
    setFolderSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setShowFolderDropdown(false);
  };

  const handleFolderHeaderOptionChange = (option) => {
    setFolderHeaderLabel(option);
    setShowFolderDropdown(false);
    if (option === 'Name') {
      setFolderSortField('name');
    } else if (option === 'Date') {
      setFolderSortField('created_at');
    } else if (option === 'Custom') {
      setFolderSortField('name');
    }
  };

  const handleDocumentSort = () => {
    setDocumentSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const handleDocumentHeaderOptionChange = (option) => {
    setDocumentHeaderLabel(option);
    setShowDocumentHeaderDropdown(false);
    if (option === 'Name') {
      setDocumentSortField('name');
    } else if (option === 'Date') {
      setDocumentSortField('created_at');
    } else if (option === 'Custom') {
      setDocumentSortField('name');
    }
  };

  const sorted = (arr, field, order) =>
    [...arr].sort((a, b) => {
      const va =
        (a[field] !== undefined && a[field] !== null
          ? a[field]
          : ''
        ).toString().toLowerCase();
      const vb =
        (b[field] !== undefined && b[field] !== null
          ? b[field]
          : ''
        ).toString().toLowerCase();
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return order === 'asc' ? cmp : -cmp;
    });

  const sortedFolders = sorted(folders, folderSortField, folderSortOrder);
  const sortedDocuments = sorted(
    documents,
    documentSortField,
    documentSortOrder
  );

  const formatDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString() : 'N/A';

  /* SESSION START / FINISH */
  

  const confirmFinishSession = () => setShowFinishConfirmPopup(true);

  const finishSessionHandler = async () => {
  try {
    setIsLoading(true);

    await finishSession(sessionDetails.sessionId);
    setSessionStatus('finished');

    setShowFinishConfirmPopup(false);

    // 👇 Instead of directly closing → open share docs popup
    setShowShareDocsPopup(true);
    setSelectedDocs([]);
  } catch (err) {
    console.error(err);
    toast.error(displayText('Failed to finish session'));
  } finally {
    setIsLoading(false);
  }
};

  /* PASSCODE POPUP */
  const openPasscodePopup = (e) => {
    e.stopPropagation();
    if (sessionStatus !== 'started') return;
    const rect = passcodeIconRef.current.getBoundingClientRect();
    setPasscodePos({
      top: rect.top + window.scrollY - 8,
      left: rect.right + window.scrollX + 2,
    });
    setShowPasscodePopup(true);
  };
  const closePasscodePopup = (e) => {
    if (e) e.stopPropagation();
    setShowPasscodePopup(false);
  };

  /* CLOSE DROPDOWNS ON OUTSIDE CLICK */
  useEffect(() => {
    const handler = (e) => {
      if (
        folderHeaderRef.current &&
        !folderHeaderRef.current.contains(e.target)
      ) {
        setShowFolderDropdown(false);
      }
      if (
        documentHeaderRef.current &&
        !documentHeaderRef.current.contains(e.target)
      ) {
        setShowDocumentHeaderDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* RENDER HELPERS */
  const panelWidth = 250;
  const isMobile = window.innerWidth < 600;
  const showBeginButton = canControl && sessionStatus === 'pending';
  const showFinishButton = canControl && sessionStatus === 'started';
  const showNoButtons = canControl && sessionStatus === 'finished';
  const showAttendanceToggle = sessionStatus === 'started';

  const getFileIcon = (doc) => {
    if (doc.is_placeholder) {
      return {
        icon: 'bi-file-earmark-plus',
        color: theme.colors.warning || '#f59e0b',
      };
    }

    const name = (doc.name || '').toLowerCase();
    const ext = name.split('.').pop() || '';

    switch (ext) {
      case 'pdf':
        return { icon: 'bi-file-earmark-pdf', color: '#d32f2f' };
      case 'doc':
      case 'docx':
        return { icon: 'bi-file-earmark-word', color: '#2b579a' };
      case 'xls':
      case 'xlsx':
      case 'xlsm':
      case 'csv':
        return { icon: 'bi-file-earmark-spreadsheet', color: '#1e6e3b' };
      case 'ppt':
      case 'pptx':
        return { icon: 'bi-filetype-ppt', color: '#d24726' };
      case 'jpg':
      case 'jpeg':
        return { icon: 'bi-filetype-jpg', color: theme.colors.primary || '#0d6efd' };
      case 'png':
        return { icon: 'bi-filetype-png', color: theme.colors.primary || '#0d6efd' };
      case 'gif':
        return { icon: 'bi-filetype-gif', color: theme.colors.primary || '#0d6efd' };
      case 'txt':
        return { icon: 'bi-file-earmark-text', color: theme.colors.textSecondary || '#6c757d' };
      case 'zip':
      case 'rar':
      case '7z':
        return { icon: 'bi-file-earmark-zip', color: theme.colors.warning || '#f59e0b' };
      default:
        return { icon: 'bi-file-earmark', color: theme.colors.textSecondary || '#6c757d' };
    }
  };

  if (!sessionDetails?.sessionId) return null;

  return (
    <Layout>
      <div
        style={{
          fontFamily,
          fontSize: `${bodyFontSize}px`,
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
          width: '100%',
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* TOP BAR */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.4rem 0.6rem',
            backgroundColor: theme.colors.bgPrimary,
            fontSize: `${bodyFontSize}px`,
            fontWeight: 500,
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <i
              className="bi bi-person-video3"
              style={{ color: theme.colors.primary, fontSize: '1.2em' }}
            />
            <span>
  <strong>{displayText("Case:")}</strong> {caseData?.case_name || 'Loading...'} |
  <strong>{displayText(" Session:")}</strong> {sessionData?.name || sessionData?.custom_type || sessionDetails.sessionId}{' '}
  {sessionStatus === 'started' && (
    <i
      ref={passcodeIconRef}
      className="bi bi-eye-slash"
      onClick={openPasscodePopup}
      style={{
        marginLeft: '0.4rem',
        cursor: 'pointer',
        fontSize: '1em',
      }}
      title="Show passcode"
    />
  )}
</span>
          </div>
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>

  {showInviteButton && (
    <button
      onClick={() => setShowInvitePopup(true)}
      style={{
        background: '#0d6efd',
        color: theme.colors.textWhite,
        border: 'none',
        padding: '0.4rem 0.9rem',
        borderRadius: '6px',
        fontWeight: 'bold',
        cursor: 'pointer',
        fontSize: `${Math.max(bodyFontSize - 2, 10)}px`,
      }}
    >
      Invite
    </button>
  )}

</div>
            {showBeginButton && (
              <button
                onClick={beginSession}
                disabled={isLoading}
                style={{
                  background: theme.colors.primary,
                  color: theme.colors.textWhite,
                  border: 'none',
                  padding: '0.4rem 0.9rem',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: `${Math.max(bodyFontSize - 2, 10)}px`,
                }}
              >{displayText(" Begin Session ")}</button>
            )}
            {showFinishButton && (
              <button
                onClick={confirmFinishSession}
                disabled={isLoading}
                style={{
                  background: theme.colors.danger,
                  color: theme.colors.textWhite,
                  border: 'none',
                  padding: '0.4rem 0.9rem',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: `${Math.max(bodyFontSize - 2, 10)}px`,
                }}
              >{displayText(" Finish Session ")}</button>
            )}
            {showNoButtons && (
              <span
                style={{
                  fontSize: `${Math.max(bodyFontSize - 2, 10)}px`,
                  color: theme.colors.secondary,
                  fontWeight: 'bold',
                }}
              >{displayText(" Session Finished ")}</span>
            )}
            {!canControl && (
              <span
                style={{
                  fontSize: `${Math.max(bodyFontSize - 3, 9)}px`,
                  color: theme.colors.secondary,
                }}
              >
                Role: {sessionDetails.userRole}
              </span>
            )}
          </div>
        </div>

        {/* ATTENDANCE PANEL */}
        {showAttendanceToggle && (
          <div
            style={{
              position: 'fixed',
              top: isMobile ? 'auto' : 100,
              bottom: isMobile ? 0 : 'auto',
              right: showAttendance ? 0 : -panelWidth,
              width: isMobile ? '100%' : panelWidth,
              height: isMobile ? 'auto' : 'calc(100vh - 160px)',
              maxHeight: isMobile ? 'none' : '600px',
              backgroundColor: '#f9fafb',
              borderLeft: `1px solid ${theme.colors.borderColor}`,
              borderRadius: isMobile ? 0 : '12px 0 0 12px',
              padding: '1rem',
              boxShadow: '-4px 0 18px rgba(15,23,42,0.20)',
              zIndex: 100,
              transition: 'right 0.4s ease',
              overflowY: 'auto',
              fontFamily,
              fontSize: `${bodyFontSize}px`,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
                borderBottom: `1px solid ${theme.colors.borderColor}`,
                paddingBottom: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <i
                  className="bi bi-people-fill"
                  style={{
                    fontSize: 18,
                    color: theme.colors.primary,
                  }}
                />
                <div>
                  <h5
                    style={{
                      fontSize: `${Math.max(titleFontSize - 9, 12)}px`,
                      fontWeight: 700,
                      margin: 0,
                    }}
                  >
                    Participants ({participants.length})
                  </h5>
                  <span style={{ fontSize: `${Math.max(bodyFontSize - 4, 9)}px`, color: '#6b7280' }}>{displayText(" Live attendance for this session ")}</span>
                </div>
              </div>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {participants.length > 0 ? (
                participants.map((p) => {
                  const isSelected =
                    selectedEditorId != null &&
                    String(selectedEditorId) === String(p.id);
                  const initials =
                    (p.full_name || 'U')
                      .split(' ')
                      .map((s) => s[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase() || 'U';
                  return (
                    <li
                      key={p.id}
                      onClick={() => handleSelectEditor(p)}
                      style={{
                        padding: '0.6rem 0.45rem',
                        marginBottom: 6,
                        border: `1px solid ${theme.colors.borderColor}`, // no orange border
                        backgroundColor: '#ffffff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: `${bodyFontSize}px`,
                        cursor:
                          canControl && p.type !== 'client_admin'
                            ? 'pointer'
                            : 'default',
                        borderRadius: 10,
                        boxShadow: '0 1px 3px rgba(15,23,42,0.10)',
                        transition:
                          'background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {(p.type === 'witness' || p.type === 'guest') && (
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            disabled={!canControl || editingLocked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleSelectEditor(p)}
                            style={{
                              width: 14,
                              height: 14,
                              cursor:
                                canControl && !editingLocked
                                  ? 'pointer'
                                  : 'not-allowed',
                              borderRadius: '50%',
                              border: `2px solid ${theme.colors.borderColor}`,
                              backgroundColor: isSelected
                                ? theme.colors.primary
                                : '#ffffff',
                              outline: 'none',
                              display: 'inline-block',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              boxShadow: 'none',
                              transition:
                                'background-color 0.15s ease, border-color 0.15s ease',
                            }}
                          />
                        )}
                        {/* Avatar circle */}
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background:
                              p.type === 'client_admin'
                                ? '#facc15'
                                : p.type === 'witness'
                                ? theme.colors.primary
                                : '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 600,
                            color: theme.colors.textWhite,
                            flexShrink: 0,
                          }}
                        >
                          {initials}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: theme.colors.textPrimary,
                            }}
                          >
                            {p.full_name}
                          </span>
                          <small
                            style={{
                              color: '#6b7280',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {p.email}
                          </small>
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            padding: '0.2rem 0.55rem',
                            backgroundColor:
                              p.type === 'client_admin'
                                ? '#facc15'
                                : p.type === 'witness'
                                ? theme.colors.primary
                                : p.type === 'guest'
                                ? '#6c757d'
                                : theme.colors.secondary,
                            color: 'white',
                            borderRadius: '999px',
                            fontSize: `${Math.max(bodyFontSize - 5, 8)}px`,
                            fontWeight: 'bold',
                            letterSpacing: 0.3,
                            textTransform: 'uppercase',
                          }}
                        >
                          {p.type === 'client_admin'
                            ? 'Admin'
                            : p.type.toUpperCase()}
                        </span>
                      </div>
                    </li>
                  );
                })
              ) : (
                <li
                  style={{
                    padding: '1rem',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: `${Math.max(bodyFontSize - 2, 10)}px`,
                  }}
                >
                  No participants yet.
                </li>
              )}
            </ul>
            {/* Editor Control Buttons (Admin Only) */}
            {canControl && (
              <div
                style={{
                  marginTop: 12,
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 8,
                  justifyContent: 'space-between',
                }}
              >
                <button
                  onClick={handlePassEditing}
                  disabled={
                    isLoading || !selectedEditorId || sessionStatus !== 'started'
                  }
                  style={{
                    flex: 1,
                    padding: '0.45rem 0.6rem',
                    borderRadius: 999,
                    border: 'none',
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.textWhite,
                    fontSize: `${Math.max(bodyFontSize - 3, 9)}px`,
                    fontWeight: 600,
                    cursor: isLoading ? 'wait' : 'pointer',
                    boxShadow: '0 1px 3px rgba(15,23,42,0.3)',
                  }}
                >
                  Pass Editing
                </button>
                <button
                  onClick={handleRevokeEditing}
                  disabled={isLoading || sessionStatus !== 'started'}
                  style={{
                    flex: 1,
                    padding: '0.45rem 0.6rem',
                    borderRadius: 999,
                    border: 'none',
                    backgroundColor: theme.colors.secondary,
                    color: theme.colors.textWhite,
                    fontSize: `${Math.max(bodyFontSize - 3, 9)}px`,
                    fontWeight: 600,
                    cursor: isLoading ? 'wait' : 'pointer',
                    boxShadow: '0 1px 3px rgba(15,23,42,0.15)',
                  }}
                >
                  Revoke
                </button>
              </div>
            )}
          </div>
        )}
        {showAttendanceToggle && (
          <div
            onClick={() => setShowAttendance((v) => !v)}
            style={{
              position: 'fixed',
              top: '50%',
              right: showAttendance ? panelWidth : 10,
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              zIndex: 101,
              backgroundColor: theme.colors.primary,
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
              transition: 'right 0.4s ease',
            }}
          >
            <i
              className={`bi ${
                showAttendance ? 'bi-chevron-right' : 'bi-chevron-left'
              }`}
              style={{ fontSize: 20, color: theme.colors.textWhite }}
            />
          </div>
        )}

        {/* FOLDERS + DOCUMENTS */}
        <div
          style={{
            margin: '1rem',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            height: isMobile ? 'auto' : 500,
            minHeight: 400,
            borderRadius: 10,
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            position: 'relative',
            overflow: 'hidden',
            fontFamily,
            fontSize: `${bodyFontSize}px`,
          }}
        >
          {/* FOLDERS */}
          <div
            style={{
              width: isMobile ? '100%' : '25%',
              borderRight: isMobile
                ? 'none'
                : `1px solid ${theme.colors.borderColor}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '0.5rem 1rem',
                borderBottom: `1px solid ${theme.colors.borderColor}`,
              }}
            >
              <h4
                style={{
                  fontSize: `${Math.max(titleFontSize - 10, 12)}px`,
                  fontWeight: 'bold',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <i
                  className="bi bi-folder"
                  style={{ color: theme.colors.primary, fontSize: '1.1em' }}
                />
                <span>Folders ({folders.length})</span>
              </h4>
            </div>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.textWhite,
                  }}
                >
                  <th
                    ref={folderHeaderRef}
                    style={{
                      padding: '0.5rem 1rem',
                      textAlign: 'left',
                      position: 'relative',
                      cursor: 'default',
                      fontSize: `${bodyFontSize}px`,
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFolderDropdown((prev) => !prev);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          textDecoration: 'underline',
                          color: theme.colors.textWhite,
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        {folderHeaderLabel}
                      </span>
                      <span
                        style={{
                          cursor: 'pointer',
                          marginLeft: 4,
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFolderSortOrder();
                        }}
                      >
                        <i
                          className={`bi ${
                            folderSortOrder === 'asc'
                              ? 'bi-arrow-up'
                              : 'bi-arrow-down'
                          }`}
                          style={{
                            fontSize: '0.9em',
                            padding: '2px',
                            borderRadius: '3px',
                          }}
                        />
                      </span>
                    </div>
                    {showFolderDropdown && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: '1rem',
                          backgroundColor: theme.colors.bgPrimary,
                          border: `1px solid ${theme.colors.borderColor}`,
                          borderRadius: '4px',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                          zIndex: 10,
                          minWidth: '140px',
                          marginTop: 4,
                        }}
                      >
                        {['Name', 'Date', 'Custom'].map((option) => (
                          <div
                            key={option}
                            onClick={() => handleFolderHeaderOptionChange(option)}
                            style={{
                              width: '100%',
                              padding: '0.5rem 0.75rem',
                              backgroundColor:
                                folderHeaderLabel === option
                                  ? '#abababff'
                                  : '#ffffffff',
                              color:
                                folderHeaderLabel === option
                                  ? '#ffffff'
                                  : '#000000',
                              border: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: `${Math.max(bodyFontSize - 2, 10)}px`,
                            }}
                          >
                            {option}
                          </div>
                        ))}
                      </div>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedFolders.map((f) => (
                  <React.Fragment key={f.id}>
                    <tr
                      onClick={() => setSelectedFolder(f)}
                      style={{
                        backgroundColor:
                          selectedFolder?.id === f.id
                            ? 'rgba(101, 98, 98, 0.1)'
                            : '#ffffffff',
                        cursor: 'pointer',
                      }}
                    >
                      <td style={{ padding: '0.5rem 1rem' }}>
                        <i
                          className="bi bi-folder"
                          style={{
                            marginRight: 6,
                            color: theme.colors.primary,
                          }}
                        />
                        <span style={{ color: theme.colors.textPrimary }}>
                          {f.name}
                        </span>
                        {f.is_default && (
                          <small
                            style={{ fontStyle: 'italic', marginLeft: 4 }}
                          >
                            (Default)
                          </small>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: 0 }}>
                        <hr
                          style={{
                            border: `1px solid ${theme.colors.borderColor}`,
                            margin: 0,
                          }}
                        />
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
                {!sortedFolders.length && (
                  <tr>
                    <td style={{ padding: '0.5rem 1rem' }}>
                      No folders selected.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* DOCUMENTS */}
          <div
            style={{
              width: isMobile ? '100%' : '75%',
              position: 'relative',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '0.5rem 1rem',
                borderBottom: `1px solid ${theme.colors.borderColor}`,
              }}
            >
              <h4
                style={{
                  fontSize: `${Math.max(titleFontSize - 10, 12)}px`,
                  fontWeight: 'bold',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <i
                  className="bi bi-file-earmark"
                  style={{ color: theme.colors.primary, fontSize: '1.1em' }}
                />
                <span>Documents ({documents.length})</span>
              </h4>
            </div>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: theme.gradients.primary,
                    color: theme.colors.textWhite,
                  }}
                >
                  <th
                    ref={documentHeaderRef}
                    style={{
                      padding: '0.5rem 1rem',
                      cursor: 'default',
                      textAlign: 'left',
                      position: 'relative',
                      fontSize: `${bodyFontSize}px`,
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          textDecoration: 'underline',
                          color: theme.colors.textWhite,
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDocumentHeaderDropdown((prev) => !prev);
                        }}
                      >
                        {documentHeaderLabel}
                      </span>
                      <span
                        style={{
                          cursor: 'pointer',
                          marginLeft: 4,
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDocumentSort();
                        }}
                      >
                        <i
                          className={`bi ${
                            documentSortOrder === 'asc'
                              ? 'bi-arrow-up'
                              : 'bi-arrow-down'
                          }`}
                          style={{ marginLeft: 0 }}
                        />
                      </span>
                    </div>
                    {showDocumentHeaderDropdown && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: '1rem',
                          backgroundColor: '#ffffff',
                          border: `1px solid ${theme.colors.borderColor}`,
                          borderRadius: '4px',
                          minWidth: '140px',
                          zIndex: 10,
                          marginTop: 4,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        }}
                      >
                        {['Name', 'Date', 'Custom'].map((option) => (
                          <div
                            key={option}
                            style={{
                              padding: '0.5rem 0.75rem',
                              cursor: 'pointer',
                              backgroundColor:
                                documentHeaderLabel === option
                                  ? '#abababff'
                                  : '#ffffffff',
                              color:
                                documentHeaderLabel === option
                                  ? '#ffffff'
                                  : '#000000',
                              fontSize: `${Math.max(bodyFontSize - 2, 10)}px`,
                            }}
                            onClick={() =>
                              handleDocumentHeaderOptionChange(option)
                            }
                          >
                            {option}
                          </div>
                        ))}
                      </div>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedFolder ? (
                  sortedDocuments.length ? (
                    sortedDocuments.map((d) => {
                      const docSelected =
                        selectedDocumentId != null &&
                        selectedDocumentId === d.id;
                      const rowBg = d.is_placeholder
                        ? '#fef2f2'
                        : docSelected
                        ? 'rgba(101, 98, 98, 0.1)'
                        : '#ffffff';
                      return (
                        <React.Fragment key={d.id}>
                          <tr
  onClick={() => {
    setSelectedDocumentId(d.id);
    openInPresentation(d);
  }}
  style={{
    cursor: d.is_placeholder ? 'not-allowed' : 'pointer',
    backgroundColor: rowBg,
    opacity: d.is_placeholder ? 0.6 : 1,
  }}
>
  <td
    style={{
      padding: '0.5rem 1rem',
      position: 'relative',
      color: theme.colors.textPrimary,
      fontSize: `${bodyFontSize}px`,
    }}
  >
    <i
      className={getFileIcon(d).icon}
      style={{
        marginRight: 6,
        color: getFileIcon(d).color,
      }}
    />
    {d.name}
    <span
      style={{
        position: 'absolute',
        right: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: `${Math.max(bodyFontSize - 3, 9)}px`,
        color: '#666',
      }}
    >
      {formatDate(d.created_at || d.updated_at)}
    </span>
  </td>
</tr>
                          <tr>
                            <td style={{ padding: 0 }}>
                              <hr
                                style={{
                                  border: `1px solid ${theme.colors.borderColor}`,
                                  margin: 0,
                                }}
                              />
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: `${bodyFontSize}px`,
                        }}
                      >
                        No selected documents.
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td
                      style={{
                        padding: '0.5rem 1rem',
                        fontSize: `${bodyFontSize}px`,
                      }}
                    >
                      Select a folder.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* POPUPS */}
        <Popup
          show={showStartPopup}
          onClose={() => setShowStartPopup(false)}
          title={displayText("Session Started")}
          onConfirm={() => setShowStartPopup(false)}
          confirmText="OK"
          theme={theme}
          showOnlyConfirm={true}
        >
          <div
            style={{
              textAlign: 'left',
              fontSize: `${bodyFontSize}px`,
              lineHeight: 1.6,
              padding: '0 20px',
              fontFamily,
            }}
          >
            <p>
              <strong>{displayText("Session ID:")}</strong> {sessionDetails.sessionId}
            </p>
            <p>
              <strong>Type:</strong> {sessionData?.session_type || 'N/A'}
            </p>
            <p>
              <strong>Passcode:</strong>{' '}
              <span
                style={{
                  fontFamily: 'monospace',
                  letterSpacing: '2px',
                }}
              >
                {sessionData?.session_passcode || 'N/A'}
              </span>
            </p>
            <p>
              <strong>Started:</strong> {new Date().toLocaleString()}
            </p>
            <p
              style={{
                color: theme.colors.success,
                fontWeight: 'bold',
              }}
            >
              Participants can now join!
            </p>
          </div>
        </Popup>

        <Popup
          show={showFinishConfirmPopup}
          onClose={() => setShowFinishConfirmPopup(false)}
          title={displayText("Finish the Session?")}
          onConfirm={finishSessionHandler}
          confirmText="OK"
          cancelText="Cancel"
          theme={theme}
          isLoading={isLoading}
        >
          <p
            style={{
              fontFamily,
              fontSize: `${bodyFontSize}px`,
            }}
          >{displayText(" Are you sure you want to finish the session? It will be closed and all users should save their work before exiting. ")}</p>
        </Popup>

        <Popup
          show={showFinishSuccessPopup}
          onClose={() => {
            setShowFinishSuccessPopup(false);
            navigate('/session');
          }}
          title={displayText("Session Ending")}
          onConfirm={() => {
            setShowFinishSuccessPopup(false);
            navigate('/session');
          }}
          confirmText="OK"
          theme={theme}
          showOnlyConfirm={true}
        >
          <p
            style={{
              fontFamily,
              fontSize: `${bodyFontSize}px`,
            }}
          >{displayText(" This session has been marked as finished. Please be sure to save any annotated documents before logging out. ")}</p>
        </Popup>
        <Popup
  show={showShareDocsPopup}
  onClose={() => {
    setShowShareDocsPopup(false);
    setShowFinishSuccessPopup(true); // optional
  }}
  title="Share Documents"
  confirmText="Send Documents"
  isLoading={sendingDocs}
  onConfirm={async () => {

    if (selectedDocs.length === 0) {
      toast.warning('Select at least one document');
      return;
    }

    try {
      setSendingDocs(true);

      await shareSessionDocuments({
        session_id: sessionDetails.sessionId,
        document_ids: selectedDocs,
      });

      toast.success('Documents shared successfully');

      setShowShareDocsPopup(false);
      setShowFinishSuccessPopup(true);

    } catch (err) {
      console.error(err);
      toast.error('Failed to share documents');
    } finally {
      setSendingDocs(false);
    }
  }}
>
  <div style={{ textAlign: 'left' }}>
    <p>Select documents to send to invited users:</p>

    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
      {documents.length > 0 ? (
        documents.map(doc => (
          <div key={doc.id} style={{ marginBottom: '6px' }}>
            <label>
              <input
                type="checkbox"
                checked={selectedDocs.includes(doc.id)}
                onChange={() => {
                  setSelectedDocs(prev =>
                    prev.includes(doc.id)
                      ? prev.filter(id => id !== doc.id)
                      : [...prev, doc.id]
                  );
                }}
              />{' '}
              {doc.name || doc.file_name}
            </label>
          </div>
        ))
      ) : (
        <p>No documents available</p>
      )}
    </div>
  </div>
</Popup>

        <Popup
  show={showZoomPopup}
  onClose={() => setShowZoomPopup(false)}
  title="Zoom Meeting Ready"
  onConfirm={() => {
  if (zoomLaunchUrl) {
    window.open(zoomLaunchUrl, '_blank', 'noopener,noreferrer');
    setShowZoomPopup(false);
  }
}}
  confirmText="Join Zoom"
  cancelText="Close"
  theme={theme}
  isLoading={isLoading}
>
  <div
    style={{
      textAlign: 'left',
      fontSize: `${bodyFontSize}px`,
      lineHeight: 1.6,
      padding: '0 20px',
      fontFamily,
    }}
  >
    <p>
      <strong>{displayText("Session ID:")}</strong> {sessionDetails.sessionId}
    </p>
    <p>
      <strong>Type:</strong> {sessionData?.session_type || 'N/A'}
    </p>
    <p>
      <strong>Meeting ID:</strong> {zoomMeetingInfo?.zoom_meeting_id || 'N/A'}
    </p>
    <p>
      <strong>Password:</strong> {zoomMeetingInfo?.zoom_password || 'N/A'}
    </p>
    <p style={{ wordBreak: 'break-word' }}>
      <strong>Join URL:</strong> {zoomMeetingInfo?.zoom_join_url || 'N/A'}
    </p>
    <p style={{ color: theme.colors.success, fontWeight: 'bold' }}>
      Click Join Zoom to continue.
    </p>
  </div>
</Popup>

        {/* PASSCODE POPUP */}
        <Popup
  show={showInvitePopup}
  onClose={() => setShowInvitePopup(false)}
  title="Invite Participants"
  confirmText="Send Invite"
  isLoading={sendingInvite}
  onConfirm={async () => {
    if (!inviteEmails.trim()) {
      toast.warning('Please enter at least one email');
      return;
    }

    const emailArray = inviteEmails
      .split(',')
      .map(e => e.trim())
      .filter(Boolean);

    if (emailArray.length === 0) {
      toast.warning('Enter valid emails');
      return;
    }

    try {
      setSendingInvite(true);

      await sendSessionInvite({
  session_id: sessionDetails.sessionId,
  emails: emailArray,
  role: inviteRole,
  passcode: inviteRole === 'witness' ? sessionData?.session_passcode : null,
});

      toast.success('Invites sent successfully!');
      setShowInvitePopup(false);
      setInviteEmails('');
      setInviteRole('witness');

    } catch (err) {
      console.error(err);
      toast.error('Failed to send invites');
    } finally {
      setSendingInvite(false);
    }
  }}
>
  <div style={{ textAlign: 'left' }}>
    
    {/* Emails */}
    <label style={{ fontWeight: 'bold' }}>Emails (comma separated)</label>
    <textarea
      value={inviteEmails}
      onChange={(e) => setInviteEmails(e.target.value)}
      placeholder="example1@mail.com, example2@mail.com"
      style={{
        width: '100%',
        marginTop: '6px',
        marginBottom: '12px',
        padding: '8px',
        borderRadius: '5px',
        border: '1px solid #ccc',
      }}
    />

    {/* Role Selection */}
    <label style={{ fontWeight: 'bold' }}>Role</label>
    <select
      value={inviteRole}
      onChange={(e) => setInviteRole(e.target.value)}
      style={{
        width: '100%',
        marginTop: '6px',
        marginBottom: '12px',
        padding: '8px',
        borderRadius: '5px',
        border: '1px solid #ccc',
      }}
    >
      <option value="witness">Witness</option>
      <option value="guest">Guest</option>
    </select>

  </div>
</Popup>

</div>
    </Layout>



  );
};

export default JoinSession;
